import { Box, InputAdornment, TextField } from "@mui/material"
import { AddOutlined, FilterAltOutlined, SearchOutlined } from "@mui-symbols-material/w300"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { ChangeEvent, FC, ReactElement } from "react"

import { Button } from "@/components/common/Button"
import { PermissionActionType } from "@/constants/permissions"
import { usePermissionManager } from "@/hooks/usePermissionManager"

interface WarehouseSearchProps {
  onFilterOpen: () => void
  onCreateDialogOpen: () => void
}

export const WarehouseSearch: FC<WarehouseSearchProps> = ({ onFilterOpen, onCreateDialogOpen }): ReactElement => {
  const currentSearch = useSearch({ from: "/app/admin/warehouses/" })
  const navigate = useNavigate()
  const { hasPermissions } = usePermissionManager()

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const queryValue = e.target.value

    navigate({
      search: {
        ...currentSearch,
        query: queryValue,
      },
      from: "/app/admin/warehouses",
      replace: true,
    })
  }

  return (
    <Box className='mb-6 flex items-center justify-between gap-4' data-testid='warehouse-search-container'>
      <Box>
        <TextField
          data-testid='warehouse-search-input'
          defaultValue={currentSearch?.query}
          variant='outlined'
          placeholder='Search by Company Name, Company ID, Warehouse ID, or Address'
          onChange={handleInputChange}
          className='mr-4 min-w-[580px] max-w-[700px]'
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchOutlined className='text-gray-600' />
                </InputAdornment>
              ),
            },
          }}
        />
        <Button
          aria-label='filter'
          appearance='outlined'
          onClick={onFilterOpen}
          startIcon={<FilterAltOutlined />}
          data-testid='warehouse-filter-button'
        >
          Filter
        </Button>
      </Box>
      {hasPermissions([PermissionActionType.WAREHOUSES_MAINTENANCE]) && (
        <Button
          startIcon={<AddOutlined />}
          appearance='outlined'
          onClick={onCreateDialogOpen}
          data-testid='warehouse-add-button'
        >
          Add Warehouse
        </Button>
      )}
    </Box>
  )
}
